<template>
  <div class="payment-link-step-amount">
    <div class="step-description">
      <p>{{ t('Informe o valor que será cobrado do cliente') }}</p>
    </div>

    <div class="amount-input-container">
      <input-currency
        data-testid="payment-links-step-amount-input"
        :label="t('Valor')"
        :placeholder="t('0,00')"
        :validation="() => isValid"
        :required="true"
        elem-id="payment_link_amount"
        v-model="amountDisplay">
      </input-currency>
    </div>

    <div class="amount-preview" v-if="amountDisplay > 0">
      <span class="preview-label">{{ t('Valor a cobrar') }}:</span>
      <span class="preview-value">{{ formatCurrency(amountDisplay) }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

interface PaymentLinkFormData {
  amount?: number;
  [key: string]: unknown;
}

const { t } = useI18n();

const props = defineProps<{
  modelValue: PaymentLinkFormData;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: PaymentLinkFormData): void;
}>();

// Amount display (converts between cents and display value)
const amountDisplay = computed<number>({
  get: () => {
    return (props.modelValue.amount || 0) / 100;
  },
  set: (value: number) => {
    emit('update:modelValue', {
      ...props.modelValue,
      amount: Math.round(value * 100)
    });
  }
});

// Validation
const isValid = computed<boolean>(() => {
  return (props.modelValue.amount ?? 0) > 0;
});

// Methods
const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value || 0);
};

// Expose validation for wizard
defineExpose({
  isValid
});
</script>

<style scoped>
.payment-link-step-amount {
  padding: 1rem 0;
}

.step-description {
  margin-bottom: 1.5rem;
  color: var(--text-color-secondary);
}

.step-description p {
  margin: 0;
}

.amount-input-container {
  max-width: 300px;
}

.amount-preview {
  margin-top: 1.5rem;
  padding: 1rem;
  background-color: var(--surface-ground);
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.preview-label {
  color: var(--text-color-secondary);
  font-weight: 500;
}

.preview-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--primary-color);
}
</style>
